import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useRequest from '../../hooks/useRequest';
import {
    formatSpecialCommission,
    parseSpecialCommission,
} from '../../domain/contracts/specialRate';

export interface SpecialRateTarget {
    nr: number;
    contractNumber: string;
    commissionPerDay?: number | string;
}

interface SpecialRateDialogProps {
    /** The contract to change; `null` keeps the dialog closed. */
    contract: SpecialRateTarget | null;
    onClose: () => void;
    onSaved: (commissionPerDay: number) => void;
}

/**
 * Delphi's *Comision special* panel (BL-05): an administrator gives an open contract a new daily
 * commission, and the contract is flagged special-rate, so it gets no discount at payment.
 */
const SpecialRateDialog = ({ contract, onClose, onSaved }: SpecialRateDialogProps) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);

    useEffect(() => {
        setText(formatSpecialCommission(contract?.commissionPerDay));
        setSaveFailed(false);
    }, [contract]);

    const commissionPerDay = parseSpecialCommission(text);
    const invalid = commissionPerDay === null;

    const handleSave = async () => {
        if (!contract || commissionPerDay === null) {
            return;
        }
        setSaving(true);
        setSaveFailed(false);
        try {
            await request({
                path: `/contracts/${contract.nr}/special-rate`,
                method: 'PATCH',
                params: { commissionPerDay },
            });
            onSaved(commissionPerDay);
        } catch (error) {
            console.error('Set special commission error:', error);
            setSaveFailed(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={contract !== null} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {t('contracts.specialRate.title', { number: contract?.contractNumber ?? '' })}
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {t('contracts.specialRate.description')}
                </DialogContentText>
                <TextField
                    fullWidth
                    label={t('contracts.specialRate.commissionPerDay')}
                    value={text}
                    onChange={event => setText(event.target.value)}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            void handleSave();
                        }
                    }}
                    error={invalid && text.trim() !== ''}
                    helperText={invalid ? t('contracts.specialRate.invalid') : undefined}
                    disabled={saving}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                />
                {saveFailed && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {t('contracts.specialRate.error')}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void handleSave()}
                    disabled={invalid || saving}
                >
                    {t('contracts.specialRate.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SpecialRateDialog;
